import React, { useMemo, useState } from "react";
import { Button, Typography, Paper } from "@material-ui/core";
import { useDot } from "../../context/automatonContext";
import { useUser } from "../../context/userContext";
import simulation from "../../views/Simulate/formatList";
import "./MultiPathSimulator.css";
import colors from "../../constants/colors";
import pathConstants from "../../constants/PathSimulation/pathSimulationConstants";
import PathHistoryDialog from "./Dialogs/PathHistoryDialog";
import { MACHINE_TYPES } from "../../variables/machineVariables";
import {
  isNullOrUndefined,
  isNullOrUndefinedOrEmptyArray,
  isNullOrUndefinedOrEmptyObject,
} from "../../utils/generalValidators";

import TuringCardContent from "../MultiPathSimulator/TuringCardContent";
import StandardCardContent from "../MultiPathSimulator/StandardCardContent";

/**
 * Componente Principal: Gerencia a lógica de navegação.
 */
export default function MultiPathSimulator() {
  const { language } = useUser();
  const {
    dataTreeDerivation,
    currentComputationLevel,
    setCurrentComputationLevel,
    setIsMultiPathSimulating,
    setIsSimulating,
    responseSimulate,
    automatonType,
  } = useDot();

  const [isActivePathHistoryDialog, setIsActivePathHistoryDialog] =
    useState(false);
  const [selectedPathHistory, setSelectedPathHistory] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const [selectedPathRejected, setSelectedPathRejected] = useState(false);

  const wordTape = useMemo(() => {
    if (
      responseSimulate &&
      responseSimulate.responseSimulateAutomata &&
      responseSimulate.responseSimulateAutomata.word
    ) {
      return responseSimulate.responseSimulateAutomata.word;
    }
    return "";
  }, [responseSimulate]);

  const tapeStepsFull = useMemo(() => {
    if (
      responseSimulate &&
      responseSimulate.responseSimulateAutomata &&
      responseSimulate.responseSimulateAutomata.tape
    ) {
      return responseSimulate.responseSimulateAutomata.tape;
    }
    return [];
  }, [responseSimulate]);

  const activePaths = useMemo(() => {
    if (isNullOrUndefinedOrEmptyObject(dataTreeDerivation)) {
      return [];
    }
    return simulation.getStepsByLevel(
      dataTreeDerivation,
      currentComputationLevel
    );
  }, [dataTreeDerivation, currentComputationLevel]);

  const maxLevel = useMemo(() => {
    if (isNullOrUndefinedOrEmptyObject(dataTreeDerivation)) {
      return 0;
    }
    let max = 0;
    const findMaxLevel = (node) => {
      max = Math.max(max, node.numberComputations || 0);
      if (node.children) {
        node.children.forEach(findMaxLevel);
      }
    };
    findMaxLevel(dataTreeDerivation);
    return max;
  }, [dataTreeDerivation]);

  const handleSelectPath = (endNode) => {
    if (isNullOrUndefinedOrEmptyArray(tapeStepsFull)) {
      return;
    }

    const isRejected = endNode.status === "REJECTED";
    setSelectedPathRejected(isRejected);

    const actualName =
      endNode.name || (endNode.actualState && endNode.actualState.name);
    const correctedEndNode = {
      ...endNode,
      actualState: { name: actualName, label: actualName },
    };

    let path = [correctedEndNode];
    let currentStep = correctedEndNode;

    while (currentStep && !isNullOrUndefined(currentStep.parentId)) {
      const parentStep = tapeStepsFull.find(
        (step) => step.stepId === currentStep.parentId
      );
      if (parentStep) {
        path.push(parentStep);
        currentStep = parentStep;
      } else {
        break;
      }
    }

    const linearPath = path.sort(
      (a, b) => a.numberComputations - b.numberComputations
    );

    if (linearPath.length > 0) {
      setSelectedPathHistory(linearPath);
      setIsActivePathHistoryDialog(true);
    }
  };

  // === HANDLERS DE NAVEGAÇÃO ===

  const handleNextLevel = () => {
    if (currentComputationLevel < maxLevel) {
      setCurrentComputationLevel(currentComputationLevel + 1);
    }
  };

  const handlePreviousLevel = () => {
    if (currentComputationLevel > 0) {
      setCurrentComputationLevel(currentComputationLevel - 1);
    }
  };

  const handleStopSimulating = () => {
    setIsMultiPathSimulating(false);
    setIsSimulating(false);
    setCurrentComputationLevel(0);
  };

  // Verifica se o passo atual é o final da simulação
  const isFinalLevel = currentComputationLevel >= maxLevel;

  // Renderiza um único cartão de caminho
  const renderPathCard = (path, index) => {
    const isAccepted = path.status === "ACCEPTED";
    const isRejected = path.status === "REJECTED";

    let statusText = "";
    let statusColor = "transparent";
    let statusClassName = "";
    let statusVisible = false;

    if (isAccepted) {
      statusVisible = true;
      statusText = pathConstants.pathSimulation.status.accepted[language];
      statusColor = colors.green;
      statusClassName = "accepted";
    } else if (isRejected) {
      statusVisible = true;
      statusText = pathConstants.pathSimulation.status.rejected[language];
      statusColor = colors.red;
      statusClassName = "rejected";
    }

    const stateName = path.name || (path.actualState && path.actualState.name);

    const isTuringLike =
      automatonType === MACHINE_TYPES.MT || automatonType === MACHINE_TYPES.ALL;

    return (
      <Paper
        key={index}
        style={{
          cursor: "pointer",
          minWidth: isTuringLike ? "200px" : "100px",
          maxWidth: "400px",
        }}
        className="path-card path-card--clickable"
        onClick={() => handleSelectPath(path)}
      >
        <div className="path-card__branch-label">
          {`${pathConstants.pathSimulation.constants.branch[language]}: ${index + 1}`}
        </div>

        <div className="path-card__header">
          <Typography variant="subtitle1" component="h3">
            {pathConstants.pathSimulation.constants.currentState[language]}:
            <span
              style={{
                color: colors.simulationActiveState,
                fontWeight: "bold",
              }}
            >
              {" "}
              {stateName}
            </span>
          </Typography>
        </div>

        {isTuringLike && <TuringCardContent path={path} />}
        {!isTuringLike && (
          <StandardCardContent
            path={path}
            wordTape={wordTape}
            automatonType={automatonType}
            language={language}
          />
        )}

        {statusVisible && (
          <div className="path-card__status-container">
            <div
              className={`path-card__status ${statusClassName}`}
              style={{
                backgroundColor: statusColor,
                textAlign: "center",
              }}
            >
              {statusText}
            </div>
          </div>
        )}
      </Paper>
    );
  };

  let cardsContainerContent;
  if (activePaths.length > 0) {
    cardsContainerContent = activePaths.map(renderPathCard);
  } else {
    cardsContainerContent = null;
  }

  return (
    <div className="multi-path-simulator__wrapper">
      <Typography
        variant="h5"
        align="center"
        style={{
          fontWeight: "bold",
          marginBottom: "5px",
          textTransform: "uppercase",
        }}
      >
        {pathConstants.pathSimulation.texts.multiPathTitle[language]}
      </Typography>
      <Typography
        variant="subtitle2"
        align="center"
        style={{ marginBottom: "5px" }}
      >
        {pathConstants.pathSimulation.texts.information[language]}
      </Typography>

      <div className="multi-path-simulator__cards-container">
        {cardsContainerContent}
      </div>

      <div className="multi-path-simulator__controls">
        <Button
          variant="contained"
          color="default"
          onClick={handlePreviousLevel}
          disabled={currentComputationLevel <= 0}
        >
          {pathConstants.pathSimulation.steps.back[language]}
        </Button>

        <Typography variant="subtitle1">
          {pathConstants.pathSimulation.constants.level[language]}{" "}
          {currentComputationLevel} / {maxLevel} ({activePaths.length}{" "}
          {pathConstants.pathSimulation.constants.activePaths[language]}){" "}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={handleNextLevel}
          disabled={isFinalLevel}
        >
          {pathConstants.pathSimulation.steps.next[language]} &#9654;{" "}
        </Button>

        <Button variant="outlined" onClick={handleStopSimulating}>
          {pathConstants.pathSimulation.steps.stop[language]}{" "}
        </Button>
      </div>

      <PathHistoryDialog
        isActive={isActivePathHistoryDialog}
        onClose={() => setIsActivePathHistoryDialog(false)}
        historyList={selectedPathHistory}
        automatonType={automatonType}
      />
    </div>
  );
}
